import api from './api';

export interface LoginData {
  email: string;
  password: string;
}

export interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  password: string;
}

export interface AuthResponse {
  token: string;
  refresh_token: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    company?: string;
  };
  message: string;
  redirect_url?: string;
}

class AuthApiService {
  async login(data: LoginData): Promise<AuthResponse> {
    try {
      // Ajouter l'anonymous_id si présent pour fermer les discussions anonymes
      const anonymous_id = sessionStorage.getItem('anonymous_id');
      const loginData = anonymous_id ? { ...data, anonymous_id } : data;
      
      const response = await api.post('/api/auth/login/', loginData);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur de connexion. Veuillez réessayer.');
    }
  }

  async register(data: RegisterData): Promise<AuthResponse> {
    try {
      const response = await api.post('/api/auth/register/', data);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de l\'inscription. Veuillez réessayer.');
    }
  }

  async logout(): Promise<void> {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await api.post('/api/auth/logout/', { refresh_token: refreshToken });
      }
    } catch (error) {
      // Ignorer les erreurs de logout pour l'instant
    } finally {
      // Supprimer les tokens locaux
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    }
  }

  getCurrentUser(): any {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  isAuthenticated(): boolean {
    const token = localStorage.getItem('authToken');
    return !!token;
  }

  getToken(): string | null {
    return localStorage.getItem('authToken');
  }

  setAuthData(token: string, user: any, refreshToken?: string): void {
    localStorage.setItem('authToken', token);
    localStorage.setItem('user', JSON.stringify(user));
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
  }

  getRedirectPath(): string {
    const redirectPath = localStorage.getItem('redirectAfterLogin');
    if (redirectPath) {
      localStorage.removeItem('redirectAfterLogin');
      return redirectPath;
    }
    return '/';
  }

  clearRedirectPath(): void {
    localStorage.removeItem('redirectAfterLogin');
  }

  async forceLogout(): Promise<void> {
    // Déconnexion forcée sans appel API (pour les cas d'expiration de token)
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    
    // Optionnel : déclencher un événement pour notifier les composants
    window.dispatchEvent(new CustomEvent('auth:logout'));
  }
}

export const authApiService = new AuthApiService();
